import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import { 
  Users, Anchor, GraduationCap, Shield, BarChart3, 
  MapPin, FileText, Heart, Globe, Sparkles 
} from 'lucide-react';

const CommunitySection = () => {
  const { language } = useLanguage();

  const features = [
    {
      icon: Anchor,
      color: 'cyan',
      title: 'Piattaforma di Citizen Science',
      subtitle: 'Sentinelle del Mare',
      content: "Un'interfaccia dedicata dove pescatori, subacquei e cittadini possono segnalare in tempo reale il ritrovamento di reti fantasma o depositi di rifiuti marini. Ogni segnalazione viene geolocalizzata, alimentando direttamente la pianificazione degli interventi tecnici di Marine Innovation Tech (MIT)."
    },
    {
      icon: GraduationCap,
      color: 'emerald',
      title: 'Modulo Didattico Interattivo',
      subtitle: 'Metodo MareDu',
      content: "Uno spazio riservato alle scuole coinvolte dove gli studenti, dopo i laboratori educativi, caricano i propri \"Diari di Bordo\" e i risultati dei questionari di sensibilizzazione. Questo permette di monitorare quantitativamente l'evoluzione della sensibilità ambientale delle nuove generazioni nel tempo."
    },
    {
      icon: Shield,
      color: 'violet',
      title: 'Governance Partecipata e Trasparenza',
      subtitle: 'Cabina di Regia',
      content: "La community funge da \"Cabina di Regia\" dove le autorità locali, le associazioni e i partner tecnici condividono una visione unica dello stato di salute dei relitti e delle Tegnùe. Questo approccio garantisce la Social Acceptance del progetto, trasformando un intervento tecnico in un patrimonio culturale e sociale del territorio."
    },
    {
      icon: BarChart3,
      color: 'amber',
      title: 'Impact Reporting per Stakeholder',
      subtitle: 'Report di Impatto',
      content: "La dashboard aggrega i contributi di ogni membro della community (es. ore di volontariato, dati raccolti, segnalazioni effettuate) generando report di impatto certi. Questo strumento è fondamentale per attrarre futuri investimenti e dimostrare la scalabilità del modello in altri bacini del Mediterraneo."
    }
  ];

  const colorClasses = {
    cyan: {
      bg: 'bg-cyan-500/20',
      border: 'border-cyan-500/50',
      text: 'text-cyan-400',
      icon: 'text-cyan-400'
    },
    emerald: {
      bg: 'bg-emerald-500/20',
      border: 'border-emerald-500/50',
      text: 'text-emerald-400',
      icon: 'text-emerald-400'
    },
    violet: {
      bg: 'bg-violet-500/20',
      border: 'border-violet-500/50',
      text: 'text-violet-400',
      icon: 'text-violet-400'
    },
    amber: {
      bg: 'bg-amber-500/20',
      border: 'border-amber-500/50',
      text: 'text-amber-400',
      icon: 'text-amber-400'
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 via-blue-600 to-violet-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/30">
          <Users className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="COMMUNITY HUB" />
        </h2>
        <p className="text-cyan-400 mt-1">
          <TranslatedText text="Il Cuore Digitale di Respiro Blu" />
        </p>
        
        {/* Badge */}
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border border-cyan-500/40 rounded-full">
          <Globe className="w-4 h-4 text-cyan-400" />
          <span className="text-cyan-300 text-sm font-medium">
            <TranslatedText text="Azione Collettiva • Consapevolezza Diffusa" />
          </span>
        </div>
      </div>

      {/* Intro Box */}
      <div className="bg-slate-900/60 border border-cyan-500/30 rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg mb-2">
              <TranslatedText text="Il Modulo Community Hub" />
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              <TranslatedText text="Il cuore dell'infrastruttura digitale Respiro Blu è rappresentato dal modulo dedicato alla Community, progettato per trasformare i dati tecnici in azione collettiva e consapevolezza diffusa. In linea con i requisiti del bando relativi al coinvolgimento attivo degli stakeholder, la dashboard integra le seguenti specifiche funzionali." />
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="space-y-4">
        {features.map((feature, idx) => {
          const colors = colorClasses[feature.color];
          const Icon = feature.icon;
          
          return (
            <div 
              key={idx}
              className={`bg-slate-900/60 border ${colors.border} rounded-2xl p-5 hover:bg-slate-900/80 transition-all`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-7 h-7 ${colors.icon}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`font-bold ${colors.text}`}>
                      <TranslatedText text={feature.title} />
                    </h4>
                  </div>
                  <p className="text-white text-sm font-medium mb-2">
                    <TranslatedText text={feature.subtitle} />
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    <TranslatedText text={feature.content} />
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Call to Action */}
      <div className="mt-8 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-violet-500/10 border border-cyan-500/30 rounded-2xl p-6 text-center">
        <Heart className="w-10 h-10 text-pink-400 mx-auto mb-3" />
        <h4 className="text-white font-bold text-lg mb-2">
          <TranslatedText text="Unisciti alla Community" />
        </h4>
        <p className="text-slate-400 text-sm max-w-md mx-auto">
          <TranslatedText text="Ogni contributo conta. Che tu sia un pescatore, un subacqueo, uno studente o semplicemente un cittadino attento all'ambiente, puoi fare la differenza per il nostro Mediterraneo." />
        </p>
        
        {/* Stats Preview */}
        <div className="flex justify-center gap-6 mt-6">
          <div className="text-center">
            <MapPin className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
            <p className="text-xs text-slate-500"><TranslatedText text="Segnalazioni" /></p>
          </div>
          <div className="text-center">
            <FileText className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-xs text-slate-500"><TranslatedText text="Report ESG" /></p>
          </div>
          <div className="text-center">
            <Users className="w-5 h-5 text-violet-400 mx-auto mb-1" />
            <p className="text-xs text-slate-500"><TranslatedText text="Volontari" /></p>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <p className="text-center text-slate-500 text-xs mt-6">
        <TranslatedText text="Respiro Blu — Citizen Science per il Mediterraneo" />
      </p>
    </div>
  );
};

export default CommunitySection;
